import { Button } from "@RHCommerceDev/unstyled-ui-components/Button";
import { Typography } from "@RHCommerceDev/unstyled-ui-components/Typography";
import { RHFormConsumerTextField } from "@RHCommerceDev/component-rh-text-field/RHFormConsumerTextField";
import { usePageContentNoProxy } from "@RHCommerceDev/custom-providers/LocationProvider";
import {
  useSetAuthenticationDialogCurrentScreenAtom,
  useUserSessionSetAtom
} from "@RHCommerceDev/hooks-atoms";
import useAsync from "@RHCommerceDev/hooks/useAsync";
import memoize from "@RHCommerceDev/utils/memoize";
import { useMutation } from "@apollo/client";
import { Theme, Tooltip, useMediaQuery } from "@mui/material";
import { useStytch } from "@stytch/react";
import { stytchSuccessMutation } from "graphql-client/queries/user";
import { PasswordEyeIcon } from "icons/PasswordEyeIcon";
import { ShowPassword } from "icons/ShowPasswordIcon";
import * as React from "react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import PasswordIndicator from "../PasswordIndicator";
import { isValidatePassword } from "@RHCommerceDev/utils/stytchUtils";
import { periodLookbackRegex } from "@RHCommerceDev/utils/regex";
import useGetCartProjectLazyQuery from "@RHCommerceDev/hooks/queries/useGetCartProjectLazyQuery";
import { useIsCartPages } from "@RHCommerceDev/hooks/useIsCartPages";

const ForgotPassword = () => {
  const setAuthenticationDialogCurrentScreen =
    useSetAuthenticationDialogCurrentScreenAtom();
  const setUserSessionAtom = useUserSessionSetAtom();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [password, setPassword] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [getCartProjection] = useGetCartProjectLazyQuery();
  const { isShoppingCartPage } = useIsCartPages();

  const togglePasswordVisibility = () => {
    setPasswordVisible(prev => !prev);
  };

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setErrorMessage("");
    const value = e.target.value;
    setPassword(value);
    form.setValue("password", value, { shouldValidate: true });
  };

  const [stytchSuccess] = useMutation<Mutation>(stytchSuccessMutation, {
    context: {
      fetchOptions: {
        method: "POST"
      }
    },
    onCompleted: ({ stytchSuccess }) => {
      setUserSessionAtom(prevData => ({
        ...prevData,
        ...stytchSuccess,
        ...(isShoppingCartPage && { isCartMerge: true })
      }));
      getCartProjection({ id: stytchSuccess?.rhuid });
    },
    onError: error => {
      setErrorMessage("Failed to update Password. Please try again.");
    }
  });

  const form = useForm({
    mode: "onBlur",
    defaultValues: {
      password: ""
    }
  });
  const watch = form.watch(); // watch password field to apply styles
  const { pageContent } = usePageContentNoProxy();
  const stytchData = pageContent?.emailSignupContent;
  const stytchClient = useStytch();
  const desktopMatchesQuery = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up("sm")
  );

  const { run, loading } = useAsync(async ({ password }) => {
    try {
      const stytchUser = await stytchClient.passwords.resetBySession({
        password,
        session_duration_minutes: 129600
      });

      await stytchSuccess();
      setAuthenticationDialogCurrentScreen("UpdatedPasswordWelcomePage");
      setErrorMessage("");
    } catch (error: any) {
      if (stytchData?.Stytch_Error_Object.hasOwnProperty(error.error_type)) {
        setErrorMessage(stytchData?.Stytch_Error_Object[error.error_type]);
        return;
      } else setErrorMessage("Failed to reset password. Please try again.");
    }
  });

  const onSubmit = React.useCallback(
    values => {
      const isValidatePasswordResult = isValidatePassword(values.password);
      if (!isValidatePassword(values.password).every(Boolean)) return;
      return run(values);
    },
    [isValidatePassword, run]
  );
  const formValues = form.watch();
  const isFormComplete = Object.values(formValues).every(value => {
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return false;
  });

  function values(
    data: { password: string },
    event?: React.BaseSyntheticEvent<object, any, any> | undefined
  ) {
    throw new Error("Function not implemented.");
  }

  return (
    <div className="flex flex-col items-center">
      <Typography
        variant={"h1"}
        className="flex h-6 w-full items-center justify-center text-center font-primary-rh-thin text-xl !font-[75] leading-6 tracking-[-0.4px] "
      >
        {stytchData?.UPDATE_YOUR_PASSWORD}
      </Typography>
      <div className="mb-6 mt-6 flex flex-col">
        {stytchData?.To_enhance_your?.split(periodLookbackRegex).map(item => (
          <Typography
            className="primary-thin text-center !font-light"
            variant="p"
          >
            {item}
          </Typography>
        ))}
      </div>

      <FormProvider {...form}>
        <form
          className="flex w-[311px] flex-col"
          onSubmit={form.handleSubmit(values => {
            onSubmit(values);
          })}
        >
          <div className="flex flex-col gap-6">
            <div
              className={`${
                errorMessage ? "mb-6" : "mb-0"
              } relative h-14 w-full`}
            >
              <Tooltip
                title={<PasswordIndicator password={password} />}
                placement="right"
                arrow
                open={Boolean(password.length) && desktopMatchesQuery}
                classes={{
                  tooltip:
                    "bg-white rounded-none shadow-swatchGroupsToolTip p-[20px]",
                  arrow:
                    "!w-4 !h-5 shadow-arrow -left-2 text-[#fff] before:shadow-swatchGroupsToolTip"
                }}
              >
                <RHFormConsumerTextField
                  type={passwordVisible ? "text" : "password"}
                  defaultValue=""
                  autoComplete="off"
                  name="password"
                  inputProps={{ maxLength: 20 }}
                  onChange={handlePasswordChange}
                  fullWidth
                  label="New Password"
                  className={`testing flex justify-center ${
                    watch?.password?.length
                      ? "!border-[0.5px] [&>div>fieldset]:!border-black"
                      : ""
                  }`}
                />
              </Tooltip>
              <span
                className="absolute right-3 top-[36px] -translate-y-1/2 transform cursor-pointer p-0"
                onClick={togglePasswordVisibility}
                role="button"
                onKeyDown={e => {
                  // Ensure the button itself is focused before handling the keydown event
                  if (
                    e.currentTarget === document.activeElement &&
                    (e.key === "Enter" || e.key === " ")
                  ) {
                    e.preventDefault(); // Prevent default behavior
                    togglePasswordVisibility();
                  }
                }}
                tabIndex={0} // Ensure the button is focusable
              >
                {passwordVisible ? <ShowPassword /> : <PasswordEyeIcon />}
              </span>
              {errorMessage && (
                <Typography className="items-start justify-start font-primary-rhlight text-xxsm text-rh-red">
                  {errorMessage}
                </Typography>
              )}
            </div>

            {Boolean(password.length) && !desktopMatchesQuery && (
              <PasswordIndicator password={password} />
            )}
            <div>
              <Button
                type="submit"
                variant="secondary"
                size={"lg"}
                color="primary"
                className={` loading ? "bg-[#666] cursor-not-allowed" : "" w-full text-[#FFF]`}
                disabled={loading || !isFormComplete}
              >
                {stytchData?.UPDATE}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default memoize(ForgotPassword);
